import { useCookies } from 'react-cookie'
import { useQuery } from 'react-query'

import { SITE_COOKIE } from '@hapana/shared/constants/cookies.constant'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { getSiteId, getSiteNames } from '../services/sites.service'

import { useErrorHandler } from './errors.hooks'

export const useSiteId = ({ hasOnError = true } = { hasOnError: true }) => {
  const [cookies] = useCookies([SITE_COOKIE])
  const v1Info = cookies[SITE_COOKIE]
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  const v2SiteId = localStorage.getItem(`${v1Info?.tenant}_site_${v1Info?.v1SiteId}`)

  const onSuccess = (siteId) => {
    if (siteId && v1Info?.tenant) {
      localStorage.setItem(`${v1Info?.tenant}_site_${v1Info?.v1SiteId}`, siteId)
    }
  }

  const query = useQuery(getSiteId.key, getSiteId.query, {
    select: (data) => data.site.id,
    enabled: !!(!v2SiteId && v1Info?.v1SiteId),
    onSuccess,
    meta: {
      v1Id: v1Info?.v1SiteId,
    },
    refetchOnMount: false,
    onError: hasOnError && onError,
  })

  return v2SiteId
    ? {
        data: v2SiteId,
        isSuccess: true,
      }
    : query
}

/**
 * Resolves siteId with fallback chain:
 * 1. useSiteId() hook (cookie-based, works in check-in widget)
 * 2. URL path extraction (works in Core app where URL is /s/:siteId/...)
 */
export const useSiteIdResolved = () => {
  const { data: siteId } = useSiteId()
  if (siteId) {
    return siteId
  }
  const match = window.location.pathname.match(/\/s\/([0-9a-f-]{36})/)
  return match ? match[1] : ''
}

export const useSiteNames = (siteIds) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const { data: siteId } = useSiteId()

  // siteIds may arrive as null (e.g. GraphQL returns homeLocationIds: null for
  // clients with no home/shared locations), so normalise before use — the query
  // is built eagerly on every render regardless of `enabled`.
  const safeSiteIds = siteIds ?? []

  const siteVariables =
    safeSiteIds.length > 0 &&
    Object.fromEntries(safeSiteIds.map((siteId, index) => [`siteId${index}`, siteId]))

  return useQuery([getSiteNames().key, siteVariables], getSiteNames(safeSiteIds).query, {
    enabled: safeSiteIds.length > 0 && !safeSiteIds.includes(siteId),
    select: (data) => safeSiteIds.map((_, index) => data[`site${index}`]),
    onError,
  })
}
